import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ChevronLeft, ChevronRight, Minus, Plus, Trash } from "lucide-react";
import { Button } from "./ui/button";
import type { CartItemType, CartType } from "@/types/cart";
import type { CompanyType } from "@/types/company";
import emptyCart from "@/assets/empty-cart.json";
import Lottie from "lottie-react";
import { useTranslation } from "react-i18next";
import mealPlaceholder from "@/assets/burger_placeholder.png";

interface CartSideDrawerProps {
  open: boolean;
  onClose: () => void;
  otherCarts: CartType[];
  activeCart?: CartType;
  onDeleteCart: (cart: CartType) => void;
  onIncreaseQuantity: (cartItem: CartItemType) => void;
  onDecreaseQuantity: (cartItem: CartItemType) => void;
  onSelectCart: (cart: CartType) => void;
  onCompanyClick: (company: CompanyType) => void;
  onItemClick: (cartItem: CartItemType) => void;
  onCheckout: () => void;
}

export default function CartSideDrawer({
  open,
  onClose,
  otherCarts,
  activeCart,
  onDeleteCart,
  onIncreaseQuantity,
  onDecreaseQuantity,
  onSelectCart,
  onCompanyClick,
  onItemClick,
  onCheckout,
}: CartSideDrawerProps) {
  const { i18n, t } = useTranslation();

  console.log("direction:", i18n.dir());

  if (!activeCart && otherCarts.length === 0) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent
          className="max-w-full w-lg"
          dir={i18n.dir() === "rtl" ? "rtl" : "ltr"}
        >
          <div className="flex flex-col overflow-y-auto h-[100vh] p-4 gap-1">
            <h1 className="text-xl font-semibold">{t("your_carts")}</h1>
            <p className="text-sm text-gray-500 mb-2">
              {t("build_and_save_carts_from_multiple_restaurants")}
            </p>
            <hr />
            <div className="flex flex-col p-4 items-center justify-center h-full">
              <div className=" flex flex-col items-center justify-center ">
                <Lottie
                  animationData={emptyCart}
                  className="w-32 h-32 mb-6"
                  loop={true}
                />
                <p className="text-lg font-semibold text-primary">
                  {t("you_have_no_carts")}
                </p>
                <p className="text-xs text-gray-500">
                  {t("start_adding_items_to_your_cart")}
                </p>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        className="max-w-full w-lg"
        dir={i18n.dir() === "rtl" ? "rtl" : "ltr"}
      >
        <div className="flex flex-col overflow-y-auto h-[100vh]">
          <div className="flex flex-col p-4 gap-4">
            <div>
              <span className="text-xs text-gray-500">
                {t("your_cart_from")}
              </span>
              <div
                className="flex items-center gap-1 cursor-pointer"
                onClick={() => {
                  onCompanyClick(activeCart?.company!);
                }}
              >
                <span className="text-xl font-semibold">
                  {activeCart?.company?.name}
                </span>
                {i18n.dir() === "rtl" ? (
                  <ChevronLeft className="size-4" />
                ) : (
                  <ChevronRight className="size-4" />
                )}
              </div>
            </div>
            <Button size="lg" className="rounded-xl" onClick={onCheckout}>
              {t("continue")}
            </Button>
            {activeCart?.cartItems.map((cartItem) => (
              <>
                <hr />
                <div
                  className="flex gap-4 items-center hover:bg-gray-100 rounded-lg p-2 cursor-pointer"
                  onClick={() => {
                    onItemClick(cartItem);
                  }}
                >
                  <img
                    src={cartItem.item.image || mealPlaceholder}
                    className="w-16 h-16 object-cover"
                  />
                  <div className="flex flex-col gap-2">
                    <div>
                      <h1 className="leading-relaxed">{cartItem.item.name}</h1>
                      <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">
                        {cartItem.ingredients.map((ing) => ing.name).join(", ")}
                      </p>
                    </div>

                    <span className="text-sm font-semibold">
                      ₪
                      {(cartItem.item.price +
                        cartItem.ingredients.reduce(
                          (acc, ing) => acc + ing.price,
                          0
                        )) *
                        cartItem.quantity}
                    </span>
                  </div>
                  <div className="flex ms-auto items-center gap-2 bg-[#F7F7F7] rounded-full border border-gray-200 shadow-sm">
                    <div
                      className="bg-white rounded-full h-8 w-8 flex justify-center items-center"
                      onClick={() => {
                        onDecreaseQuantity(cartItem);
                      }}
                    >
                      {cartItem.quantity === 1 ? (
                        <Trash className="size-3" strokeWidth={4} />
                      ) : (
                        <Minus className="size-3" strokeWidth={4} />
                      )}
                    </div>
                    <p className="text-sm font-semibold">
                      {cartItem.quantity}&nbsp;×
                    </p>
                    <div
                      className="bg-white rounded-full h-8 w-8 flex justify-center items-center"
                      onClick={() => {
                        onIncreaseQuantity(cartItem);
                      }}
                    >
                      <Plus className="size-3" strokeWidth={4} />
                    </div>
                  </div>
                </div>
              </>
            ))}
          </div>

          {otherCarts.length > 0 && (
            <div className=" bg-gray-100 flex-1">
              <div className="p-4 flex flex-col gap-4">
                <h1 className="text-xl font-semibold">{t("other_carts")}</h1>

                {otherCarts.map((cart) => (
                  <div
                    className="bg-white flex flex-col items-center gap-4 p-4 rounded-lg hover:scale-101 hover:ring hover:ring-primary transition-all duration-300 cursor-pointer"
                    onClick={() => {
                      onSelectCart(cart);
                    }}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <img
                        src={cart.company.image}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200"
                      />
                      <h1 className="text-sm font-semibold">
                        {cart.company.name}
                      </h1>
                      <div className="hover:bg-gray-100 rounded-full h-8 w-8 flex items-center justify-center cursor-pointer ms-auto">
                        <Trash
                          className="size-4"
                          onClick={() => {
                            onDeleteCart(cart);
                          }}
                        />
                      </div>
                    </div>

                    <div className="flex gap-6 w-full items-center justify-center">
                      {cart.cartItems.slice(0, 4).map((cartItem, index) => (
                        <img
                          key={index}
                          src={cartItem.item.image || undefined}
                          className="w-12 h-12 object-cover"
                        />
                      ))}
                      {cart.cartItems.length > 4 && (
                        <p>+{cart.cartItems.length - 4}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
